import React from 'react'
import { useTranslation } from 'react-i18next'

import { StyledButton } from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { type BrandGroup } from '~/hooks/useSurveyForSettingsQuery'
import { exists } from '~/utils'

import { useAddMappingObservation } from '../hooks/useAddMappingObservation'
import { useMapping } from '../hooks/useMapping'

export type MappingCellProps = {
  dataItem: BrandGroup
}

export const MappingCell = ({ dataItem: brandGroup }: MappingCellProps) => {
  const { t } = useTranslation()
  const [isMappingLoading, setIsMappingLoading] = React.useState(false)
  const mapping = useMapping()
  const notify = useNotify()
  const logEvent = useLogEvent()
  const addMappingObservation = useAddMappingObservation()

  const handleExecute = React.useCallback(async () => {
    setIsMappingLoading(true)
    const result = await mapping(brandGroup)
    if (result.success) {
      notify({ content: t('mapping_process_started'), style: 'info' })
      logEvent('Execute 9segs data mapping')
      addMappingObservation({
        awaitId: result.awaitId,
        brandGroup,
        onMappingComplete: () => {
          setIsMappingLoading(false)
        },
      })
    } else {
      notify({ content: result.error, style: 'error' })
      setIsMappingLoading(false)
    }
  }, [addMappingObservation, brandGroup, logEvent, mapping, notify, t])

  return (
    <td>
      <VStack align="start" backgroundColor="transparent" spacing={0}>
        <StyledButton
          buttonStyle="alert"
          disabled={brandGroup.mappingInProgress || isMappingLoading}
          onClick={handleExecute}
        >
          {brandGroup.mappingInProgress || isMappingLoading
            ? t('mapping_in_progress')
            : t('response_data_mapping')}
        </StyledButton>

        {exists(brandGroup.mappedAt) ? (
          <Typography fontSize={14} variant="normal">
            {t('last_mapping_date')}
            {new Date(Number(brandGroup.mappedAt)).toLocaleString()}
          </Typography>
        ) : (
          <>
            <Typography color="error" fontSize={14} variant="normal">
              {t('not_implemented')}
            </Typography>
          </>
        )}
      </VStack>
    </td>
  )
}
